// feature/remanentes-pendientes — "Casos sin respuesta" panel, under the Informe
// Mensual section of the exports page. Admin + directora.
//
// feature/casos-sin-respuesta-vigentes — a mode toggle turns the SAME tool (same
// base filter: unanswered tutela/requerimiento/desacato, using the raw legal
// deadline, not the SLA timer) into two views:
//   • Vencidos (remanentes)        — deadline already passed, per arrival month.
//   • Vigentes (aún respondibles)  — deadline still in the future, WHOLE contract
//        (closeout: "what can we still save before time / the contract runs out").
//
// ⚠ Neither is the billing REMANENTE (that's "Exportar REMANENTE" = completed
// work). The explanatory line says so, so no one confuses the two.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { exportsApi } from "../../api/endpoints";
import { useToast } from "../../components/Toast";

const MES_ES = ["", "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

// Motivo display order + tone (mirrors lib/casos_sin_respuesta.js).
const MOTIVOS = [
  ["insumo_solicitado_no_recibido", "Insumo solicitado, no recibido", "var(--color-warning)"],
  ["insumo_recibido_sin_respuesta", "Insumo recibido, sin respuesta", "var(--color-info)"],
  ["insumo_nunca_solicitado",       "Insumo nunca solicitado",        "var(--text-muted)"],
  ["otro",                          "Otro (sin trámite de insumo)",   "var(--text-muted)"],
];

function previousMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(month) {
  const [y, m] = (month || "").split("-").map(Number);
  return m ? `${MES_ES[m]} ${y}` : month;
}
function fmtDMY(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  const b = new Date(dt.getTime() - 5 * 3600 * 1000); // Bogotá calendar date
  return `${String(b.getUTCDate()).padStart(2, "0")}/${String(b.getUTCMonth() + 1).padStart(2, "0")}/${b.getUTCFullYear()}`;
}
// Bogotá (UTC-5) YYYY-MM-DD for the vigentes snapshot filename (matches backend).
function bogotaStamp() {
  return new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 10);
}
// Días-restantes urgency color (vigentes); vencidos días are always overdue → danger.
function diasColor(isVig, r) {
  if (!isVig) return "var(--color-danger)";
  if (r.dias_restantes <= 1) return "var(--color-danger)";
  if (r.dias_restantes <= 2) return "var(--color-warning)";
  return "var(--text-body)";
}

export default function RemanentesMesPanel() {
  const toast = useToast();
  const [mode, setMode] = useState("vencidos"); // 'vencidos' | 'vigentes'
  const [month, setMonth] = useState(previousMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState(false);
  const isVig = mode === "vigentes";

  const load = useCallback(() => {
    setLoading(true);
    // Vigentes = whole contract (omit month). Vencidos = month-scoped.
    const params = isVig ? { mode: "vigentes" } : { mode: "vencidos", month };
    exportsApi.casosSinRespuesta(params)
      .then((r) => setData(r.data))
      .catch(() => { setData(null); toast.push("No se pudo cargar el conteo", "error"); })
      .finally(() => setLoading(false));
  }, [isVig, month, toast]);
  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;
  const label = useMemo(() => (isVig ? "todo el contrato" : monthLabel(month)), [isVig, month]);

  async function download() {
    setDl(true);
    try {
      const params = isVig ? { mode: "vigentes" } : { mode: "vencidos", month };
      const r = await exportsApi.casosSinRespuestaXlsx(params);
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      if (isVig) {
        a.download = `casos_vigentes_cierre_${bogotaStamp()}.xlsx`;
      } else {
        const [y, m] = month.split("-");
        a.download = `remanentes_${MES_ES[Number(m)]}_${y}.xlsx`;
      }
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.push("Excel descargado", "success");
    } catch (e) {
      toast.push("No se pudo generar el archivo", "error");
    } finally { setDl(false); }
  }

  return (
    <div id="casos-sin-respuesta" className="card" style={{ padding: 20 }}>
      <h2 style={{ margin: 0, fontSize: 16 }}>
        {isVig ? "Casos vigentes sin respuesta" : "Remanentes del mes"}
      </h2>

      {/* Mode toggle — the safe/retrospective view (vencidos) is the default. */}
      <div style={{ display: "inline-flex", borderRadius: "var(--r-btn)", overflow: "hidden", border: "1px solid var(--border-hairline)", marginTop: 12 }}>
        <button className={!isVig ? "" : "ghost"} style={{ borderRadius: 0, height: 34 }} onClick={() => setMode("vencidos")}>
          Vencidos (remanentes)
        </button>
        <button className={isVig ? "" : "ghost"} style={{ borderRadius: 0, height: 34 }} onClick={() => setMode("vigentes")}>
          Vigentes (aún respondibles)
        </button>
      </div>

      {/* Explanatory line — the distinction must be unmistakable. */}
      <div className="muted" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.5 }}>
        <strong>Vencidos:</strong> casos sin respuesta cuyo plazo legal ya venció.{" "}
        <strong>Vigentes:</strong> casos sin respuesta cuyo plazo legal aún no vence — todavía se pueden responder.
        <span style={{ color: "var(--text-hint)" }}> No es el REMANENTE de facturación (ese es trabajo ya contestado).</span>
      </div>

      <div className="row" style={{ gap: 12, alignItems: "flex-end", marginTop: 14, flexWrap: "wrap" }}>
        {isVig ? (
          <div className="col" style={{ gap: 4 }}>
            <label style={{ fontSize: 12 }}>Alcance</label>
            <div className="muted" style={{ fontSize: 13, padding: "6px 0" }}>Todo el contrato (casos aún abiertos hoy)</div>
          </div>
        ) : (
          <div className="col" style={{ gap: 4 }}>
            <label style={{ fontSize: 12 }}>Mes</label>
            <input type="month" value={month} max={previousMonth()} onChange={(e) => setMonth(e.target.value)} />
          </div>
        )}
        <button onClick={download} disabled={dl || loading || !summary || summary.total === 0}>
          {dl ? "Generando…" : (isVig ? "Descargar casos vigentes (.xlsx)" : "Descargar remanentes (.xlsx)")}
        </button>
        {summary && summary.total === 0 && !loading && (
          <span className="muted" style={{ fontSize: 12.5 }}>
            {isVig ? "No hay casos vigentes sin respuesta ahora." : `Sin remanentes en ${label}.`}
          </span>
        )}
      </div>

      {/* Live counts */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
        <div className="card" style={{ padding: "12px 16px", minWidth: 130 }}>
          <div className="tnum" style={{ fontSize: 26, fontWeight: 700 }}>
            {loading ? <span className="skeleton" style={{ width: 40, height: 24 }} /> : (summary?.total ?? "—")}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>{isVig ? "Total (todo el contrato)" : `Total en ${label}`}</div>
        </div>
        {isVig && (
          <div className="card" style={{ padding: "12px 16px", minWidth: 150, flex: "1 1 150px" }}>
            <div className="tnum" style={{ fontSize: 22, fontWeight: 700, color: "var(--color-danger)" }}>
              {loading ? <span className="skeleton" style={{ width: 30, height: 20 }} /> : (summary?.urgent_lt_48h ?? 0)}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>Menos de 48 h de plazo</div>
          </div>
        )}
        {MOTIVOS.map(([key, lbl, color]) => (
          <div key={key} className="card" style={{ padding: "12px 16px", minWidth: 150, flex: "1 1 150px" }}>
            <div className="tnum" style={{ fontSize: 22, fontWeight: 700, color }}>
              {loading ? <span className="skeleton" style={{ width: 30, height: 20 }} /> : (summary?.per_motivo?.[key] ?? 0)}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* Full per-case list on screen. The download carries the full factura columns. */}
      {!loading && summary && summary.total > 0 && (
        <>
          <div className="row" style={{ marginTop: 16, alignItems: "baseline" }}>
            <h3 style={{ margin: 0, fontSize: 14 }}>Detalle de los casos</h3>
            <span className="muted tnum" style={{ fontSize: 12, marginLeft: 8 }}>
              {data.rows.length} caso{data.rows.length === 1 ? "" : "s"}
            </span>
            <span className="muted" style={{ fontSize: 11.5, marginLeft: "auto" }}>
              {isVig
                ? "Ordenados por urgencia — el más próximo a vencer, primero."
                : "El Excel trae todas las columnas de la factura (siniestro, SOAT, placa, vinculados, etc.)."}
            </span>
          </div>
          <div className="card" style={{ padding: 0, marginTop: 8, overflow: "auto", maxHeight: 480 }}>
            <table style={{ fontSize: 12.5, minWidth: 900 }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th>Caso</th><th>Radicado</th><th>Tipo</th><th>Accionante</th>
                  <th>Juzgado</th><th>Ingreso</th><th>Límite</th>
                  <th>{isVig ? "Días rest." : "Días venc."}</th><th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.case_id}>
                    <td><strong>{r.case_number}</strong></td>
                    <td style={{ fontFamily: "monospace", fontSize: 11.5 }}>{r.radicado}</td>
                    <td>{r.tipo}</td>
                    <td>{r.accionante || "—"}</td>
                    <td style={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r.juzgado || ""}>{r.juzgado || "—"}</td>
                    <td className="muted tnum" style={{ whiteSpace: "nowrap" }}>{fmtDMY(r.fecha_ingreso)}</td>
                    <td className="muted tnum" style={{ whiteSpace: "nowrap" }}>{fmtDMY(r.deadline)}</td>
                    <td className="tnum" style={{ color: diasColor(isVig, r), fontWeight: 600, textAlign: "right" }}>
                      {isVig ? r.dias_restantes : r.dias_vencido}
                    </td>
                    <td>{r.motivo_label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Vencidos-only footnote: SLA-paused cases the "vencidos" counter hides. */}
      {!isVig && summary && summary.paused_count > 0 && (
        <div className="banner-info" style={{ marginTop: 12, fontSize: 12 }}>
          {summary.paused_count} de estos casos están "esperando insumo": el contador de vencidos no los
          muestra, pero legalmente ya están vencidos, por eso aparecen aquí.
        </div>
      )}
      {summary && summary.no_deadline_count > 0 && (
        <div className="muted" style={{ marginTop: 8, fontSize: 11.5 }}>
          Nota: {summary.no_deadline_count} caso(s) sin contestar{isVig ? "" : ` de ${label}`} no tienen término
          registrado, así que no pueden clasificarse por plazo y no aparecen en la lista.
        </div>
      )}
    </div>
  );
}
